import { useParams, useNavigate } from 'react-router-dom';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ArrowLeft, User, Phone, Clock, CheckCircle, AlertCircle, PlayCircle, PauseCircle, MessageSquare, Eye } from 'lucide-react';
import { Card, CardContent, Button } from '@caruso/ui';
import { flowsService } from '@/services/flows.service';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useEffect, useRef } from 'react';

export function FlowExecutionsList() {
  const { flowId } = useParams<{ flowId: string }>();
  const navigate = useNavigate();
  const observerTarget = useRef<HTMLDivElement>(null);

  const { data: flow } = useQuery({
    queryKey: ['flow', flowId],
    queryFn: () => flowsService.getById(flowId!),
    enabled: !!flowId,
  });

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery({
    queryKey: ['flow-executions-list', flowId],
    queryFn: ({ pageParam = 1 }) =>
      flowsService.getExecutions({
        flowId: flowId!,
        page: pageParam,
        limit: 20,
      }),
    getNextPageParam: (lastPage) => {
      if (lastPage.meta.page < lastPage.meta.totalPages) {
        return lastPage.meta.page + 1;
      }
      return undefined;
    },
    enabled: !!flowId,
    initialPageParam: 1,
  });

  // Intersection Observer para scroll infinito
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { className: string; icon: any; label: string }> = {
      pending: {
        className: 'bg-brand-ivory/10 text-brand-ivory/70 border-brand-ivory/20',
        icon: Clock,
        label: 'Pendente',
      },
      running: {
        className: 'bg-brand-gold/10 text-brand-gold border-brand-gold/20',
        icon: PlayCircle,
        label: 'Em andamento',
      },
      completed: {
        className: 'bg-green-500/10 text-green-400 border-green-500/20',
        icon: CheckCircle,
        label: 'Concluído',
      },
      failed: {
        className: 'bg-red-500/10 text-red-400 border-red-500/20',
        icon: AlertCircle,
        label: 'Falhou',
      },
      paused: {
        className: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
        icon: PauseCircle,
        label: 'Pausado',
      },
      waiting_response: {
        className: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
        icon: MessageSquare,
        label: 'Aguardando',
      },
    };

    const config = variants[status] || variants.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border ${config.className}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const formatDateTime = (date: string | undefined) => {
    if (!date) return '-';
    return format(new Date(date), "dd/MM/yyyy HH:mm", { locale: ptBR });
  };

  const allExecutions = data?.pages.flatMap(page => page.data) || [];
  const totalExecutions = data?.pages[0]?.meta.total || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/app/fluxos')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-4xl font-bold text-white">Execuções do Fluxo</h1>
            <p className="text-brand-ivory/80 mt-1 text-lg">{flow?.name}</p>
            <p className="text-brand-ivory/60 mt-1 text-sm">
              {totalExecutions} {totalExecutions === 1 ? 'execução' : 'execuções'}
            </p>
          </div>
        </div>
      </div>

      {/* Lista de Execuções */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <p className="text-white">Carregando execuções...</p>
        </div>
      ) : allExecutions.length === 0 ? (
        <Card className="bg-brand-smoke border-brand-gold/20">
          <CardContent className="py-12 text-center">
            <MessageSquare className="w-12 h-12 text-brand-ivory/30 mx-auto mb-4" />
            <p className="text-brand-ivory/60">Nenhuma execução encontrada</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {allExecutions.map((execution) => (
            <Card
              key={execution.id}
              className="bg-brand-smoke border-brand-gold/20 hover:border-brand-gold/40 transition-all cursor-pointer"
              onClick={() => navigate(`/app/fluxos/execucoes/${execution.id}`)}
            >
              <CardContent className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-center">
                  {/* Nome do Cliente */}
                  <div className="md:col-span-2">
                    <p className="text-brand-ivory/60 text-xs mb-1">Cliente</p>
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-brand-gold" />
                      <span className="text-white font-medium">
                        {execution.client?.name || 'Cliente sem nome'}
                      </span>
                    </div>
                    {execution.client?.phone && (
                      <div className="flex items-center gap-2 text-xs text-brand-ivory/60 mt-1 ml-6">
                        <Phone className="w-3 h-3" />
                        <span>{execution.client.phone}</span>
                      </div>
                    )}
                  </div>

                  {/* Data de Início */}
                  <div>
                    <p className="text-brand-ivory/60 text-xs mb-1">Início</p>
                    <p className="text-white text-sm">
                      {execution.startedAt ? formatDateTime(execution.startedAt) : '-'}
                    </p>
                  </div>

                  {/* Status */}
                  <div>
                    <p className="text-brand-ivory/60 text-xs mb-1">Status</p>
                    {getStatusBadge(execution.status)}
                  </div>

                  {/* Data de Término */}
                  <div>
                    <p className="text-brand-ivory/60 text-xs mb-1">Término</p>
                    <p className="text-white text-sm">
                      {execution.completedAt ? formatDateTime(execution.completedAt) : '-'}
                    </p>
                  </div>

                  {/* Última Ação / Step Atual */}
                  <div className="flex flex-col gap-2">
                    <div>
                      <p className="text-brand-ivory/60 text-xs mb-1">Última Ação</p>
                      <p className="text-white text-sm">{formatDateTime(execution.updatedAt)}</p>
                    </div>
                    {execution.currentNodeId && (
                      <div>
                        <p className="text-brand-ivory/60 text-xs mb-1">Step Atual</p>
                        <p className="text-brand-gold text-xs font-mono truncate">
                          {execution.currentNodeId}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Erro */}
                {execution.error && (
                  <div className="mt-3 pt-3 border-t border-brand-gold/10">
                    <div className="text-sm text-red-400 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      <span className="truncate">{execution.error}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}

          {/* Loading indicator para scroll infinito */}
          <div ref={observerTarget} className="py-4 text-center">
            {isFetchingNextPage && (
              <p className="text-brand-ivory/60">Carregando mais execuções...</p>
            )}
            {!hasNextPage && allExecutions.length > 0 && (
              <p className="text-brand-ivory/40 text-sm">Todas as execuções foram carregadas</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
